import React from "react"
import styled from "styled-components"
import { useHistory } from "react-router-dom"
import { ModalOverlay } from "components/_blueprint/Modal"
import { Heading, Paragraph } from "components/_blueprint/Typography"
import { Button } from "components/atoms"
import { ApplicationSummaryEnhanced, SfPurchaseType } from "models/ApplicationSummaryEnhanced"
import { ToCurrency } from "lib/CurrencyFormatter"
import { FormatDate } from "lib/Date"
import devices from "styles/devices"
import { AppStageBadge } from "./AppStageBadge"
import { redirectToDiscovery } from "lib/discovery"

interface ActiveApplicationsModalProps {
  application?: ApplicationSummaryEnhanced
  onClose: () => void
  config: {
    heading: string
    paragraphs: string[]
    buttonText: string
    buttonAction: "view-application" | "start-new-application"
    subtext?: string
    showApplicationCard?: boolean
  }
}

export const ActiveApplicationsModal: React.FC<ActiveApplicationsModalProps> = ({
  application,
  onClose,
  config,
}) => {
  const history = useHistory()

  const handleButtonClick = () => {
    if (config.buttonAction === "view-application") {
      if (application) {
        onClose()
        history.push(`/application/${application.friendlyId}/status`)
      } else {
        onClose()
      }
    } else if (config.buttonAction === "start-new-application") {
      onClose()
      redirectToDiscovery()
    }
  }

  return (
    <ModalOverlay
      ariaLabel={config.heading}
      onClose={onClose}
      variant="light"
      darkerOverlay={true}
      slideUpOnMobile={true}
    >
      <ModalContent>
        <Heading as="h4" size="h4">
          {config.heading}
        </Heading>

        {config.paragraphs.map((paragraph, index) => (
          <Paragraph key={index}>{paragraph}</Paragraph>
        ))}

        {config.showApplicationCard && application && (
          <ApplicationSummaryCard application={application} />
        )}

        <Button text={config.buttonText} onClick={handleButtonClick} />
        {config.subtext && <Subtext>{config.subtext}</Subtext>}
      </ModalContent>
    </ModalOverlay>
  )
}

const ApplicationSummaryCard = ({ application }) => {
  const getApplicationSubtype = () => {
    const { applicationReason, occupancy, purchaseType } = application

    let type: string | undefined

    if (occupancy === "Rent_it_out") {
      type = "Buy to Let"
    } else if (occupancy === "Live_in_property") {
      if (purchaseType === SfPurchaseType.FirstTimeBuyer) type = "First Time Buyer"
      if (purchaseType === SfPurchaseType.MovingHome) type = "Home Mover"
      if (purchaseType === SfPurchaseType.SecondHome) type = "Second Home"
    }

    if (!type && purchaseType) {
      if (purchaseType === SfPurchaseType.FirstTimeBuyer) type = "First Time Buyer"
      if (purchaseType === SfPurchaseType.MovingHome) type = "Home Mover"
      if (purchaseType === SfPurchaseType.SecondHome) type = "Second Home"
    }

    if (applicationReason === "Remortgage") {
      return "Remortgage"
    }

    return type ? `Purchase • ${type}` : "Purchase"
  }

  const getSpecialistInitials = (name: string) => {
    if (!name) return ""
    const parts = name.split(" ")
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const specialistName = application.owner?.name || application.appointments?.[0]?.assignee
  const mortgageExpertImage = application.owner?.mortgageExpertImage
  const amount = application.purchasePrice
    ? ToCurrency(application.purchasePrice, "TBC")
    : ToCurrency(application.mortgageBalance, "TBC")
  return (
    <ApplicationCard>
      <CardHeader>
        <FriendlyId>{application.friendlyId}</FriendlyId>
        <AppStageBadge application={application} />
      </CardHeader>

      <Heading as="h5" size="h5">
        {getApplicationSubtype()}
      </Heading>

      <PropertyDetails>
        <Paragraph margin={0}>
          <strong>Property: </strong>{" "}
          {application.propertyAddress?.nameNumber
            ? `${application.propertyAddress.nameNumber}`.trim()
            : "TBC"}
        </Paragraph>
        <Paragraph margin={0}>
          <strong>Amount: </strong> {amount}
        </Paragraph>
        <Paragraph margin={0}>
          <strong>Term: </strong> {application.term ? `${application.term} years` : "TBC"}
        </Paragraph>
      </PropertyDetails>

      {specialistName && (
        <SpecialistContainer>
          <SpecialistInfo>
            {mortgageExpertImage ? (
              <img
                src={mortgageExpertImage}
                alt="your mortgage expert"
                aria-label={"your mortgage expert"}
              ></img>
            ) : (
              <SpecialistAvatar>{getSpecialistInitials(specialistName)}</SpecialistAvatar>
            )}
            <SpecialistName>{specialistName}</SpecialistName>
          </SpecialistInfo>
        </SpecialistContainer>
      )}

      <CreatedDate>
        <strong>Created:</strong> {FormatDate(application.createdOn, "Do MMMM YYYY")}
      </CreatedDate>
    </ApplicationCard>
  )
}

const ModalContent = styled.div({
  maxWidth: "600px",
  margin: "0 auto",
})

const ApplicationCard = styled.div(({ theme }) => ({
  borderRadius: "16px",
  padding: "24px",
  margin: "24px 0",
  border: `1px solid ${theme.colors.border.light}`,
  [devices.bp_md]: {
    padding: "32px",
  },
}))

const CardHeader = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "16px",
  flexWrap: "wrap",
})

const FriendlyId = styled.div(({ theme }) => ({
  background: theme.colors.text.primary,
  color: "white",
  padding: "8px 16px",
  borderRadius: "6px",
  fontWeight: 600,
  fontSize: "14px",
}))

const PropertyDetails = styled.div({
  display: "flex",
  gap: "16px",
})

const SpecialistContainer = styled.div({
  paddingTop: "16px",
})

const SpecialistInfo = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  width: "fit-content",
  borderRadius: "32px",
  padding: "8px 16px",
  background: theme.colors.neutral[100],
}))

const SpecialistAvatar = styled.div(({ theme }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: theme.colors.vivid[100],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
  color: theme.colors.primary.one,
}))

const SpecialistName = styled.span(({ theme }) => ({
  fontSize: "14px",
  color: theme.colors.text.primary,
  fontWeight: 500,
}))

const CreatedDate = styled.div(({ theme }) => ({
  fontSize: "12px",
  color: theme.colors.text.secondary,
  marginTop: "16px",
  paddingTop: "16px",
}))

const Subtext = styled.p(({ theme }) => ({
  fontSize: "14px",
  color: theme.colors.text.secondary,
  margin: "12px 0 0 0",
  lineHeight: "1.5",
}))
